import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {observer} from 'mobx-react-lite';
import {faqStore} from 'app/store/global';
import {scrollToTop} from 'app/utils';
import SmartFullPageLoader from 'app/components/reusable/smart_loaders/SmartFullPageLoader';
import {getFaqCategoryIcon} from 'app/utils/knowledgeCenter/categoryIcons';
import KnowledgeCenterArticleLayout from './KnowledgeCenterArticleLayout';
import KnowledgeCenterArticlePanel from './KnowledgeCenterArticlePanel';
import KnowledgeCenterEmptyState from './KnowledgeCenterEmptyState';

const isCategoryResolutionPending = (
    categorySlug: string,
    categoryKnown: boolean,
    summaryFetchStarted: boolean
): boolean => {
    if (!categorySlug || categoryKnown) {
        return false;
    }

    return faqStore.categoryMetaLoading ||
        faqStore.summaryLoading ||
        !summaryFetchStarted;
};

interface CategoryMainProps {
    categoryKey: string;
    categoryLabel: string;
}

const KnowledgeCenterCategoryMain = observer(({categoryKey, categoryLabel}: CategoryMainProps): React.ReactElement => {
    const iconSrc = getFaqCategoryIcon(categoryKey);
    const articles = faqStore.faqsByCategory[categoryKey] ?? [];

    return (
        <KnowledgeCenterArticleLayout
            category={categoryKey}
            categories={faqStore.faqCategories}
            layoutVariant="category"
        >
            <section className="knowledge-center__category-main">
                <KnowledgeCenterArticlePanel
                    title={categoryLabel}
                    categoryKey={categoryKey}
                    icon={iconSrc
                        ? <img
                            className="knowledge-center__category-icon-img"
                            src={iconSrc}
                            alt={categoryLabel}
                            width={26}
                            height={26}
                            aria-hidden
                        />
                        : null}
                    articles={articles.map(({name, title}) => ({name, title}))}
                />
            </section>
        </KnowledgeCenterArticleLayout>
    );
});

const useKnowledgeCenterCategoryRoute = (category: string | undefined): {
    categoryKnown: boolean;
    categorySlug: string;
    summaryFetchStarted: boolean;
} => {
    const [summaryFetchStarted, setSummaryFetchStarted] = useState(
        () => faqStore.summaryFaqs.length > 0 || faqStore.faqs.length > 0
    );

    useEffect(() => {
        faqStore.loadMeta().catch(() => null);
    }, []);

    useEffect(() => {
        if (faqStore.summaryFaqs.length > 0) {
            return;
        }

        setSummaryFetchStarted(true);
        faqStore.loadSummary();
    }, []);

    useEffect(() => {
        if (!category) {
            return;
        }

        scrollToTop();
    }, [category]);

    const categorySlug = category ?? '';
    const categoryKnown = Boolean(categorySlug && faqStore.isKnownCategory(categorySlug));

    return {categoryKnown, categorySlug, summaryFetchStarted};
};

const KnowledgeCenterCategoryPage = observer((): React.ReactElement => {
    const {category} = useParams<{category: string}>();
    const {categoryKnown, categorySlug, summaryFetchStarted} = useKnowledgeCenterCategoryRoute(category);

    if (isCategoryResolutionPending(categorySlug, categoryKnown, summaryFetchStarted)) {
        return <SmartFullPageLoader />;
    }

    const categoryKey = categoryKnown ? categorySlug : null;

    if (!categoryKey) {
        return <KnowledgeCenterEmptyState />;
    }

    const hasListData = faqStore.summaryFaqs.length > 0 || faqStore.faqs.length > 0;
    const isLoading = !hasListData && (!summaryFetchStarted || faqStore.summaryLoading);
    const categoryLabel = faqStore.resolvedCategoryOptions.find(option => option.key === categoryKey)?.label ??
        categoryKey;

    if (isLoading) {
        return <SmartFullPageLoader />;
    }

    return <KnowledgeCenterCategoryMain categoryKey={categoryKey} categoryLabel={categoryLabel} />;
});

export default KnowledgeCenterCategoryPage;
